import { useState } from "react";
import { Check, Copy, FileJson } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@wa/ui";
import type { DomainCheck } from "../../../../lib/domainCheck";

/**
 * Raw source payloads behind a domain check: the untouched WHOIS text, the RDAP
 * JSON and every DNS answer including per-record errors.
 *
 * The card above shows the fields a salesperson needs; this is the escape hatch
 * for when the parsed view looks wrong or someone needs a detail we do not
 * surface (glue records, DNSSEC, contact blocks).
 */
export function DomainRawDialog({
  check,
  open,
  onOpenChange,
  t,
}: {
  check: DomainCheck;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const whois = check.raw.whois;
  const rdap = check.raw.rdap ? JSON.stringify(check.raw.rdap, null, 2) : null;
  const dns = check.raw.dns ? JSON.stringify(check.raw.dns, null, 2) : null;

  // Open on whichever source actually answered - .ee gives WHOIS, gTLDs give RDAP.
  const initial = whois ? "whois" : rdap ? "rdap" : "dns";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileJson className="size-4 text-primary" />
            {t("ariregister.dns.rawTitle", { domain: check.domain })}
          </DialogTitle>
          <DialogDescription>{t("ariregister.dns.rawSubtitle")}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={initial} className="flex min-h-0 flex-1 flex-col">
          <TabsList>
            <TabsTrigger value="whois">
              {t("ariregister.dns.rawWhois")}
              {!whois && <Badge variant="outline" className="ml-1.5">0</Badge>}
            </TabsTrigger>
            <TabsTrigger value="rdap">
              {t("ariregister.dns.rawRdap")}
              {!rdap && <Badge variant="outline" className="ml-1.5">0</Badge>}
            </TabsTrigger>
            <TabsTrigger value="dns">{t("ariregister.dns.rawDns")}</TabsTrigger>
          </TabsList>

          <RawPane value="whois" text={whois} t={t} />
          <RawPane value="rdap" text={rdap} t={t} />
          <RawPane value="dns" text={dns} t={t} />
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function RawPane({
  value,
  text,
  t,
}: {
  value: string;
  text: string | null;
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked in some embedded contexts; the text stays selectable.
    }
  }

  return (
    <TabsContent value={value} className="mt-3 min-h-0 flex-1">
      {!text ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t("ariregister.dns.rawEmpty")}
        </p>
      ) : (
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={copy}
            className="absolute right-2 top-2 z-10"
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {t(copied ? "ariregister.dns.copied" : "ariregister.dns.copy")}
          </Button>
          <pre className="max-h-[60vh] overflow-auto rounded-lg border border-border bg-muted/40 p-4 pt-12 font-mono text-xs leading-relaxed">
            {text}
          </pre>
        </div>
      )}
    </TabsContent>
  );
}
